import { SG_EVENT_NAMES } from '@/utils/tools/segment/eventNames';
import { trackEvent } from '@/utils/tools/segment/tracking';
import { captureException } from '@sentry/nextjs';
import { Alert, Button, Carousel, Divider, Form, Input, Space, Typography } from 'antd';
import { signIn } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import GradinetContainer from '../../layouts/gradinet-container/GradinetContainer';
import PasswordInput from '../password-input/PasswordInput';
import styles from './LoginForm.module.css';

async function createUser(email, password) {
   const res = await fetch('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      headers: {
         'Content-Type': 'application/json',
      },
   });
   const data = await res.json();
   if (!res.ok) {
      throw new Error(data.message || 'Something went wrong!');
   }

   return data;
}

const emailRules = [
   { required: true, message: 'Please enter your email' },
   { type: 'email', message: 'Please enter a valid email' },
];

const passwordRules = [{ required: true, message: 'Please enter your password' }];

const confirmPasswordRules = [
   { required: true, message: 'Please confirm your password' },
   ({ getFieldValue }) => ({
      validator(_, value) {
         if (!value || getFieldValue('password') === value) {
            return Promise.resolve();
         }
         return Promise.reject(new Error('Passwords do not match!'));
      },
   }),
];

function LoginForm({ login }) {
   const clientReviews = useSWR(`${process.env.NEXT_PUBLIC_PREMAST_API_URL}/reviews`);
   const [form] = Form.useForm();
   const [errorMessage, setErrorMessage] = useState(null);
   const [loading, setLoading] = useState(false);
   const router = useRouter();
   const { redirect } = router.query;

   useEffect(() => {
      form.resetFields();
      setErrorMessage(null);
   }, [login, form]);

   async function handleFinish(values) {
      const { email, password } = values;
      setLoading(true);
      setErrorMessage(null);

      if (login) {
         const res = await signIn('credentials', {
            redirect: false,
            email,
            password,
         });
         if (!res.ok) {
            setErrorMessage(res.error);
            setLoading(false);
            trackEvent(SG_EVENT_NAMES.USER_SIGN_IN_FAILED, { email, error: res.error });
         } else {
            trackEvent(SG_EVENT_NAMES.USER_SIGN_IN, { email });

            const { redirect: redirectParam, ...remainingQuery } = router.query;

            if (router.query.ai_assistant) {
               localStorage.setItem('ai_assistant', 'true');
            }

            const destination = redirectParam && redirectParam !== 'undefined' ? redirectParam : '/dashboard';

            const queryString = new URLSearchParams(remainingQuery).toString();
            const fullDestination = queryString ? `${destination}?${queryString}` : destination;

            router.push(fullDestination);
         }
         return;
      }

      try {
         const result = await createUser(email, password);
         if (result) {
            const res = await signIn('credentials', {
               redirect: false,
               email,
               password,
            });
            if (!res.ok) {
               setErrorMessage(res.error);
               setLoading(false);
               trackEvent(SG_EVENT_NAMES.USER_SIGN_UP_FAILED, { email, error: res.error });
               return;
            }
            trackEvent(SG_EVENT_NAMES.USER_SIGN_UP, { email });

            const { redirect: redirectParam, ...remainingQuery } = router.query;

            if (router.query.ai_assistant) {
               localStorage.setItem('ai_assistant', 'true');
            }

            const destination = !redirectParam || redirectParam === 'undefined' ? '/dashboard' : redirectParam;

            const queryString = new URLSearchParams(remainingQuery).toString();
            const fullDestination = queryString ? `${destination}?${queryString}` : destination;

            router.push(fullDestination);
         }
      } catch (error) {
         captureException(error);
         setErrorMessage(error.message);
         setLoading(false);
         trackEvent(SG_EVENT_NAMES.USER_SIGN_UP_FAILED, { email, error: error.message });
      }
   }

   async function handleGoogleLogin() {
      console.log('we are at google');
      try {
         await signIn('google', {
            redirect: false,
         });
      } catch (error) {
         console.log(error);
      }
   }

   return (
      <div className={styles.wrapper}>
         <div className={styles.formContainer}>
            <div className={styles.formHeader}>
               <h1>{login ? 'Welcome Back!' : 'Create an Account'}</h1>
               <Link
                  className={styles.switchForm}
                  href={{
                     pathname: login ? '/sign-up' : '/login',
                     query: {
                        redirect: redirect || '/',
                        ...router.query,
                     },
                  }}
               >
                  {login ? "Don't have an account? Sign up" : 'Already have an account? sign in'}
               </Link>
            </div>

            <Divider />
            <Form
               form={form}
               layout="vertical"
               colon={false}
               requiredMark={false}
               onFinish={handleFinish}
               style={{ width: '100%' }}
               size="large"
            >
               {login ? <LoginFormFields loading={loading} /> : <SignupFormFields loading={loading} />}

               <Button
                  type="default"
                  htmlType="button"
                  block
                  size="large"
                  className={styles.googleButton}
                  onClick={handleGoogleLogin}
               >
                  <Space className={styles.googleButtonInner}>
                     <Image src="/images/google.png" alt="google" width={16} height={16} />
                     {login ? 'Sign In' : 'Sign Up'} with Google
                  </Space>
               </Button>
            </Form>
            {errorMessage && <Alert data-cy="alert" type="error" message={errorMessage} showIcon />}
         </div>
         <div className={styles.sideSection}>
            <GradinetContainer>
               <div className={styles.inner}>
                  <div className={styles.logo}>
                     <Image src="/images/logo-light.svg" alt="logo" width={150} height={70} />
                     <h1>A Whole New World Built For Presentations</h1>
                     <p>Better quality, bigger options, faster outcomes and sleeker designs!</p>
                  </div>
                  <Carousel autoplay>
                     {clientReviews.data?.map((review) => (
                        <div key={review.id} className={styles.review}>
                           <h4>{review.review}</h4>
                           <div className={styles.clientInfo}>
                              {review?.picture !== null && (
                                 <Image src={review?.picture} alt="client" width={50} height={50} />
                              )}
                              <div>
                                 <h5>{review.name}</h5>
                                 <p>{review.job}</p>
                              </div>
                           </div>
                        </div>
                     ))}
                  </Carousel>
               </div>
            </GradinetContainer>
         </div>
      </div>
   );
}

function LoginFormFields({ loading }) {
   return (
      <>
         <Form.Item
            name="email"
            label="Email"
            htmlFor="email"
            rules={emailRules}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={{ width: '100%', marginBottom: 0 }}
         >
            <Input id="email" type="email" placeholder="email@company.com" style={{ width: '100%' }} />
         </Form.Item>
         <PasswordInput id="login-password" formField="password" name="password" rules={passwordRules} />

         <Link href="/forgot-password">
            <Typography.Text type="secondary" className={styles.switchForm}>
               Forgot Password?
            </Typography.Text>
         </Link>
         <Button type="primary" size="large" htmlType="submit" loading={loading} disabled={loading} block>
            Sign In
         </Button>
      </>
   );
}

function SignupFormFields({ loading }) {
   return (
      <>
         <Form.Item
            name="email"
            label="Email"
            htmlFor="email"
            rules={emailRules}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
            style={{ width: '100%', marginBottom: 0 }}
         >
            <Input id="email" type="email" placeholder="email@company.com" style={{ width: '100%' }} />
         </Form.Item>
         <PasswordInput id="signup-password" formField="password" name="password" rules={passwordRules} />
         <PasswordInput
            id="signup-confirm-password"
            formField="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            placeholder="confirm password"
            dependencies={['password']}
            rules={confirmPasswordRules}
         />

         <Button type="primary" size="large" htmlType="submit" loading={loading} disabled={loading} block>
            Sign Up
         </Button>
      </>
   );
}

export default LoginForm;
